import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = ['textarea', 'preview', 'uploadProgress']
  static values = { engagementId: String }
  
  connect() {
    super.connect()
    this.setupEventListeners()
    
    // Initialize preview if targets are available
    if (this.hasPreviewTarget && this.hasTextareaTarget) {
      this.updatePreview()
    }
  }

  setupEventListeners() {
    // Drag and drop directly on textarea
    this.textareaTarget.addEventListener('dragover', this.handleDragOver.bind(this))
    this.textareaTarget.addEventListener('dragleave', this.handleDragLeave.bind(this))
    this.textareaTarget.addEventListener('drop', this.handleDrop.bind(this))
    
    // Paste event on textarea
    this.textareaTarget.addEventListener('paste', this.handlePaste.bind(this))
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
    this.textareaTarget.classList.add('ring-2', 'ring-indigo-500', 'ring-opacity-50')
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    this.textareaTarget.classList.remove('ring-2', 'ring-indigo-500', 'ring-opacity-50')
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.textareaTarget.classList.remove('ring-2', 'ring-indigo-500', 'ring-opacity-50')
    
    const files = Array.from(e.dataTransfer.files)
    this.processFiles(files)
  }

  handlePaste(e) {
    const items = Array.from(e.clipboardData.items)
    const imageItems = items.filter(item => item.type.indexOf('image') !== -1)
    
    if (imageItems.length > 0) {
      e.preventDefault()
      imageItems.forEach(item => {
        const file = item.getAsFile()
        if (file) {
          this.processFiles([file])
        }
      })
    }
  }


  async processFiles(files) {
    const imageFiles = files.filter(file => file.type.startsWith('image/'))
    
    for (const file of imageFiles) {
      await this.uploadAndInsertImage(file)
    }
  }

  async uploadAndInsertImage(file) {
    const textarea = this.textareaTarget
    const cursorPos = textarea.selectionStart
    const textBefore = textarea.value.substring(0, cursorPos)
    const textAfter = textarea.value.substring(cursorPos)
    
    // Create unique placeholder with timestamp to avoid conflicts
    const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const placeholder = `![Uploading ${file.name}...](${uniqueId})`
    
    // Insert placeholder
    textarea.value = textBefore + placeholder + textAfter
    
    // Set cursor position after placeholder
    const placeholderEnd = cursorPos + placeholder.length
    textarea.setSelectionRange(placeholderEnd, placeholderEnd)
    
    // Trigger input event
    textarea.dispatchEvent(new Event('input', { bubbles: true }))
    
    try {
      // Upload to Cloudflare R2
      const uploadResult = await this.uploadImageToR2(file)
      
      // Create the final markdown with R2 URL
      const imageMarkdown = `![${file.name}](${uploadResult.url})`
      
      // Get current value and replace the specific placeholder
      const currentValue = textarea.value
      const newValue = currentValue.replace(placeholder, imageMarkdown)
      
      // Update textarea
      textarea.value = newValue
      
      // Calculate new cursor position
      const newCursorPos = cursorPos + imageMarkdown.length
      textarea.setSelectionRange(newCursorPos, newCursorPos)
      textarea.focus()
      
      // Trigger input event
      textarea.dispatchEvent(new Event('input', { bubbles: true }))
      
    } catch (error) {
      console.error('Error uploading image:', error)
      
      // Remove the specific placeholder on error
      const currentValue = textarea.value
      textarea.value = currentValue.replace(placeholder, '')
      
      // Reset cursor position
      textarea.setSelectionRange(cursorPos, cursorPos)
      textarea.focus()
      
      this.showErrorMessage(`Failed to upload image "${file.name}": ${error.message}`)
    }
  }

  async uploadImageToR2(file) {
    const formData = new FormData()
    formData.append('image', file)
    
    // Add engagement_id if available
    if (this.hasEngagementIdValue) {
      formData.append('engagement_id', this.engagementIdValue)
    }
    
    const response = await fetch('/markdown_images', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: formData
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Upload failed with status: ${response.status}`)
    }
    
    const result = await response.json()
    
    if (!result.success) {
      throw new Error(result.error || 'Upload failed')
    }
    
    return result
  }



  showErrorMessage(message) {
    this.showNotification(message, 'error')
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div')
    notification.className = `fixed bottom-4 right-4 px-4 py-2 rounded-md text-white ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    } transition-opacity duration-300 z-50`
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.style.opacity = '0'
      setTimeout(() => notification.remove(), 300)
    }, 3000)
  }

  // Live preview functionality
  updatePreview() {
    // Try to find preview element by multiple methods
    let preview = null
    
    if (this.hasPreviewTarget) {
      preview = this.previewTarget
    } else {
      // Fallback: try to find preview element by ID or class
      preview = document.getElementById('live-preview') || document.getElementById('inloop-live-preview')
    }
    
    if (!preview) {
      return
    }
    
    const textarea = this.textareaTarget
    const markdownContent = textarea.value
    
    if (markdownContent.trim() === '') {
      preview.innerHTML = '<p class="text-gray-500 italic">Start typing to see preview...</p>'
      return
    }
    
    // Convert markdown to HTML using a simple client-side approach
    // For now, we'll just do basic replacements since we don't have a full markdown parser
    let html = this.simpleMarkdownToHtml(markdownContent)
    
    preview.innerHTML = html
  }
  
  simpleMarkdownToHtml(markdown) {
    return markdown
      // Headers
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-gray-900 mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-semibold text-gray-900 mt-6 mb-3">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-gray-900 mt-8 mb-4">$1</h1>')
      
      // Bold and italic
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      
      // Images
      .replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" class="max-w-full h-auto rounded shadow-sm my-2" />')
      
      // Links
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline" target="_blank">$1</a>')
      
      // Code blocks (simple)
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 rounded p-3 my-2 overflow-x-auto"><code class="text-sm">$1</code></pre>')
      
      // Inline code
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
      
      // Line breaks
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br>')
      
      // Wrap in paragraphs
      .replace(/^(.*)$/gm, '<p class="mb-2">$1</p>')
      
      // Clean up extra paragraph tags
      .replace(/<p class="mb-2"><\/p>/g, '')
      .replace(/<p class="mb-2">(<h[1-6])/g, '$1')
      .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
  }
}